"use client";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { readCookie } from "@zapier/cookies";
import { createContext, useRef, useState, useEffect, useContext } from "react";
import { fetchAccounts, normalizeAccounts } from "../../data/accounts.js";
import { fetchHeaderData } from "../../data/cdn.js";
import { fallbackHeaderData, selectHeaderVariant } from "../../data/header.js";
import { fetchProfiles } from "../../data/profiles.js";
import { fetchSession } from "../../data/session.js";
import atoms from "../../styles/atoms.module.scss.js";
import { emitComponentLoadEvent, injectExperiment, emitLinkClickEvent, emitUserInteractionEvent } from "../../utils/tracking.js";
import { resolveURL } from "../../utils/url.js";
import { ErrorBoundary } from "../ErrorBoundary.js";
import { Logo } from "../Logo.js";
import { AccountMenu } from "./AccountMenu/AccountMenu.js";
import { Button } from "./Button/Button.js";
import buttonStyles from "./Button/Button.module.scss.js";
import styles from "./Header.module.scss.js";
import { Link } from "./Link/Link.js";
import linkStyles from "./Link/Link.module.scss.js";
import { MobileNav } from "./MobileNav/MobileNav.js";
import { Nav } from "./Nav/Nav.js";
const HeaderContext = createContext(null);
const useHeaderContext = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error(
      "useHeaderContext must be used within a HeaderContext.Provider"
    );
  }
  return context;
};
function HeaderInner(props) {
  var _a;
  const emittedLoadEvent = useRef(false);
  const experiment = useRef(void 0);
  const [session, setSession] = useState();
  const [accounts, setAccounts] = useState();
  const [data, setData] = useState(
    ((_a = props.data) == null ? void 0 : _a.type) === "Header" ? props.data : fallbackHeaderData
  );
  useEffect(() => {
    const handleData = (data2) => {
      if ((data2 == null ? void 0 : data2.type) === "HeaderExperiment") {
        const variant = selectHeaderVariant(data2);
        setData(variant.data);
        if (variant.experiment && variant.name) {
          experiment.current = {
            name: variant.experiment,
            variant: variant.name
          };
        }
      } else if (data2) {
        setData(data2);
      }
    };
    const emitComponentLoadEvent$1 = () => {
      if (!emittedLoadEvent.current) {
        emitComponentLoadEvent(
          injectExperiment(experiment.current, {
            component_name: "Header"
          })
        );
        emittedLoadEvent.current = true;
      }
    };
    if (props.disableDataFetching) {
      handleData(props.data);
      emitComponentLoadEvent$1();
    } else {
      fetchHeaderData({ baseURL: props.dataBaseURL, staging: props.staging }).then(handleData).catch((err) => console.error("[@zapier/marketing-site-layout]", err)).finally(emitComponentLoadEvent$1);
    }
  }, [props.data, props.disableDataFetching, props.dataBaseURL, props.staging]);
  useEffect(() => {
    if (props.session && props.accounts) {
      setSession(props.session);
      setAccounts(props.accounts);
    } else if (readCookie("currentAccountId")) {
      Promise.all([
        fetchSession(props.baseURL),
        fetchAccounts(props.baseURL),
        fetchProfiles(props.baseURL)
      ]).then(([session2, accounts2, profiles]) => {
        setSession(session2);
        setAccounts(normalizeAccounts(accounts2, profiles, session2));
      });
    } else {
      setSession({ is_logged_in: false });
      setAccounts([]);
    }
  }, [props.session, props.accounts, props.baseURL]);
  const resolveURL$1 = (url) => {
    return resolveURL(url, props.baseURL, props.currentPath);
  };
  const emitUserInteractionEvent$1 = (payload) => {
    emitUserInteractionEvent(injectExperiment(experiment.current, payload));
  };
  const emitLinkClickEvent$1 = (event, payload = {}) => {
    emitLinkClickEvent(event, injectExperiment(experiment.current, payload));
  };
  return /* @__PURE__ */ jsx(
    HeaderContext.Provider,
    {
      value: {
        resolveURL: resolveURL$1,
        emitUserInteractionEvent: emitUserInteractionEvent$1,
        emitLinkClickEvent: emitLinkClickEvent$1
      },
      children: /* @__PURE__ */ jsxs("header", { className: styles.root, "data-testid": "header", children: [
        /* @__PURE__ */ jsxs("div", { className: `${atoms.flex} ${atoms.alignCenter} ${atoms.gap16}`, children: [
          /* @__PURE__ */ jsx(
            "a",
            {
              href: props.mainID ?? "#main",
              className: styles.skip,
              "data-testid": "skip-link",
              onClick: (e) => {
                emitLinkClickEvent$1(e, {
                  interaction_name: "header_skip_click"
                });
              },
              children: "Skip to content"
            }
          ),
          /* @__PURE__ */ jsx(
            "a",
            {
              className: styles.logo,
              "aria-label": "Zapier",
              href: resolveURL$1("/"),
              onClick: (e) => {
                emitLinkClickEvent$1(e, {
                  interaction_name: "header_logo_click"
                });
              },
              children: /* @__PURE__ */ jsx(Logo, {})
            }
          ),
          /* @__PURE__ */ jsx("div", { className: atoms.desktopOnly, children: /* @__PURE__ */ jsx(Nav, { label: "Main site navigation", navItems: data.mainNavItems }) })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: `${atoms.flex} ${atoms.alignCenter} ${atoms.gap4}`, children: [
          data.secondaryNavItems && data.secondaryNavItems.length > 0 && /* @__PURE__ */ jsx("div", { className: atoms.desktopOnly, children: /* @__PURE__ */ jsx(
            Nav,
            {
              label: "Secondary site navigation",
              navItems: data.secondaryNavItems,
              baseURL: props.dataBaseURL,
              localeSelector: data.localeSelector,
              staging: props.staging
            }
          ) }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: `${atoms.flex} ${atoms.alignCenter} ${atoms.gap8}`,
              "data-testid": "auth-links",
              children: session && accounts && (session.is_logged_in ? /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("span", { className: atoms.desktopOnly, children: /* @__PURE__ */ jsx(
                  Button,
                  {
                    href: resolveURL$1("/app/zaps"),
                    eventPayload: {
                      interaction_name: "header_my_zaps_click"
                    },
                    children: /* @__PURE__ */ jsx("span", { children: "My Zaps" })
                  }
                ) }),
                /* @__PURE__ */ jsx(AccountMenu, { session, accounts: accounts ?? [] })
              ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("span", { className: atoms.desktopOnly, children: /* @__PURE__ */ jsx(
                  Link,
                  {
                    label: "Log in",
                    url: resolveURL$1("/app/login"),
                    eventPayload: { interaction_name: "header_log_in_click" }
                  }
                ) }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    href: resolveURL$1("/sign-up"),
                    eventPayload: {
                      interaction_name: "header_sign_up_click"
                    },
                    children: "Sign up"
                  }
                )
              ] }))
            }
          ),
          /* @__PURE__ */ jsx("div", { className: atoms.mobileOnly, children: /* @__PURE__ */ jsx(
            MobileNav,
            {
              primaryNavItems: data.mainNavItems,
              secondaryNavItems: data.secondaryNavItems,
              localeSelector: data.localeSelector,
              isLoggedIn: session == null ? void 0 : session.is_logged_in,
              baseURL: props.baseURL,
              staging: props.staging
            }
          ) })
        ] })
      ] })
    }
  );
}
function FallbackHeader() {
  return /* @__PURE__ */ jsxs("header", { className: styles.root, children: [
    /* @__PURE__ */ jsx("a", { className: styles.logo, "aria-label": "Zapier", href: "https://zapier.com", children: /* @__PURE__ */ jsx(Logo, {}) }),
    /* @__PURE__ */ jsxs("div", { className: `${atoms.flex} ${atoms.alignCenter} ${atoms.gap8}`, children: [
      /* @__PURE__ */ jsx("a", { className: linkStyles.link, href: "https://zapier.com/app/login", children: "Log in" }),
      /* @__PURE__ */ jsx("a", { className: buttonStyles.root, href: "https://zapier.com/sign-up", children: "Sign up" })
    ] })
  ] });
}
function Header(props) {
  return /* @__PURE__ */ jsx(ErrorBoundary, { name: "Header", renderFallback: () => /* @__PURE__ */ jsx(FallbackHeader, {}), children: /* @__PURE__ */ jsx(HeaderInner, { ...props }) });
}
export {
  FallbackHeader,
  Header,
  useHeaderContext
};
